import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Switch } from '@/components/ui/Switch'
import { SCHOOL_MODULES, SCHOOL_MODULE_META, type SchoolModuleKey } from '@/features/school/modules'
import { cn } from '@/utils/cn'

/** One switch per optional module; the core (people, academics, portals) is always on. */
export function ModulePicker({
  value,
  onChange,
  disabled,
  columns = 2,
}: {
  value: SchoolModuleKey[]
  onChange: (next: SchoolModuleKey[]) => void
  disabled?: boolean
  columns?: 1 | 2
}) {
  const { t } = useTranslation('platform')
  const base = useId()
  const toggle = (key: SchoolModuleKey, on: boolean) =>
    onChange(SCHOOL_MODULES.filter((m) => (m === key ? on : value.includes(m))))
  return (
    <ul className={cn('grid gap-2', columns === 2 && 'sm:grid-cols-2')}>
      {SCHOOL_MODULES.map((key) => {
        const meta = SCHOOL_MODULE_META[key]
        const Icon = meta.icon
        const on = value.includes(key)
        const id = `${base}-${key}`
        return (
          <li
            key={key}
            className={cn(
              'flex items-start gap-3 rounded-control border px-3 py-2.5 transition-colors',
              on ? 'border-brand-200 bg-brand-50/40' : 'border-line bg-surface',
            )}
          >
            <Icon
              className={cn('mt-0.5 size-4 shrink-0', on ? 'text-brand-600' : 'text-muted')}
              aria-hidden="true"
            />
            <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
              <span className="block text-sm font-medium text-ink">{t(meta.label)}</span>
              <span className="block text-xs text-muted">{t(meta.description)}</span>
            </label>
            <Switch id={id} checked={on} disabled={disabled} onCheckedChange={(c) => toggle(key, c)} />
          </li>
        )
      })}
    </ul>
  )
}
